import { Button, Card, Col, Row, Typography } from 'antd';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory } from 'react-router';

import { AsyncCallbackButton } from '../../../components/data/asyncActionElements';
import { InlineLink } from '../../../components/typography';
import ViewQuoteBreakdownDialog from '../../../dialogs/ViewQuoteBreakdownDialog';
import routes from '../../../app/routes';
import { CurrencyFormat } from '../../../components/domainSpecific/currencyElements';
import { QuoteConversionDialog } from '../../../dialogs/QuoteConversion';
import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import QuoteSummaryOverview from '../../../widgets/quotes/QuoteSummaryOverview';
import {
  FAIcon,
  FATypes,
} from '../../../components/adapters/fontAwesomeAdapters';
import { DateFormatMedium } from '../../../utils/dateFormatting';
import { useQuoteStyles } from '../../../styles/quote.styles';
import { useQuoteSummaryHeaderStyles } from './QuoteSummaryHeader.styles';

function QuoteButtons({ onSubmit, referenceNo }) {
  const { styles } = useQuoteSummaryHeaderStyles();
  const { push } = useHistory();
  const respCtx = useResponsiveQueries();
  const { xs, sm } = respCtx;
  const isSm = sm || xs;

  const onDismissClick = () => {
    push(generatePath(routes.monitor));
  };

  return (
    <div className={styles.quoteButtonsContainer}>
      {referenceNo && (
        <Button
          className={classNames(styles.quoteButton, styles.saveButton)}
          type="secondary"
          onClick={() => {
            push(
              generatePath(routes.quoting.editQuote, {
                referenceNo,
              })
            );
          }}
        >
          <FormattedMessage id={isSm ? 'quote.review-short' : 'quote.review'} />
        </Button>
      )}

      <AsyncCallbackButton
        className={classNames(styles.quoteButton, styles.continueButton)}
        type="primary"
        callback={onSubmit}
      >
        <FormattedMessage id="book.newQuote.continue" />
      </AsyncCallbackButton>

      <AsyncCallbackButton
        className={classNames(styles.quoteButton, styles.cancelButton)}
        type="default"
        onClick={onDismissClick}
      >
        <FormattedMessage id="buttons.close" />
        <FAIcon icon="times-circle" type={FATypes.REGULAR} />
      </AsyncCallbackButton>
    </div>
  );
}

export function QuoteSummaryHeader({
  referenceNo,
  quote,
  quoteTitle = 'book.newQuote.resultTitle',
}) {
  const { styles: sharedStyles } = useQuoteStyles();
  const { styles } = useQuoteSummaryHeaderStyles();
  const [isBreakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [isConversionDialogOpen, setConversionDialogOpen] = useState(false);
  const { push } = useHistory();

  // This callback is triggered when the "Continue to booking" button is clicked.
  const handleContinue = useCallback(async () => {
    setConversionDialogOpen(true);
  }, []);

  // When the red OK button is clicked, redirect to the New Shipment page
  // and pass along the quote data to prefill the shipment form.
  const handleConversionOk = useCallback(() => {
    setConversionDialogOpen(false);
    push(`/book/new-shipment/from-address?referenceNo=${referenceNo}`);
  }, [push, referenceNo]);

  return (
    <div>
      {referenceNo && (
        <div style={{ display: 'flex' }}>
          <div className={sharedStyles.quoteIdLabel}>
            <FormattedMessage id="book.quote.quoteId" />
            <span className={sharedStyles.quoteId}>{referenceNo}</span>
          </div>
          <div className={sharedStyles.quoteIdLabel}>
            <span className={sharedStyles.quoteId}>
              <span style={{ marginRight: 3 }}>|</span>
              <FormattedMessage id="book.newQuote.expires" />{' '}
              <span>
                <DateFormatMedium value={moment(quote?.expiryDateTime)} />
              </span>
            </span>
          </div>
        </div>
      )}
      <Card className={sharedStyles.quoteSummaryHeaderCard}>
        <Row align="middle" justify="space-between">
          <Col lg={4}>
            <Typography.Title level={1}>
              <FormattedMessage id={quoteTitle} />
            </Typography.Title>
          </Col>
          <Col span={10} lg={10} xl={13} className="hide-md-and-smaller">
            <QuoteSummaryOverview quote={quote} />
          </Col>
          <Col lg={9} xl={6}>
            <Typography.Title level={1} className={styles.price}>
              <FormattedMessage id="book.newQuote.yourPrice" /> |{' '}
              <CurrencyFormat value={quote?.quoteTotal} />
            </Typography.Title>
            <InlineLink
              onClick={() => setBreakdownDialogOpen(true)}
              className="text-uppercase"
            >
              <FormattedMessage id="book.quote.quoteBreakdown" />
            </InlineLink>
          </Col>
        </Row>
      </Card>

      <QuoteButtons onSubmit={handleContinue} referenceNo={referenceNo} />

      <ViewQuoteBreakdownDialog
        quote={quote}
        isOpen={isBreakdownDialogOpen}
        close={() => setBreakdownDialogOpen(false)}
      />

      <QuoteConversionDialog
        isOpen={isConversionDialogOpen}
        onOk={handleConversionOk}
        onCancel={() => setConversionDialogOpen(false)}
      />
    </div>
  );
}
